/**
 * Reviewer-mode right panel for cortex/external-eval cohorts.
 *
 * Replaces the report editor (PrototypeReport) when userType === "reviewer".
 * The reviewer judges the AI's results per finding (external-eval agreement):
 *
 *   1. Per-finding Agree / Disagree / Skip (the primary action). Disagree
 *      reveals reason pills (required) before it persists. One REVIEWER_VERDICT#
 *      row per finding, optimistically updated.
 *   2. An overall case verdict (right / wrong / borderline) + free-text note,
 *      persisted as the existing DISPOSITION# row — this is what the worklist's
 *      reviewed indicator reads.
 *
 * The finding list is the canonical positive set `detected_at_recommended`, so
 * the panel never re-derives an operating point. Predictions supply the
 * slice-jump target and the OP badge only. No original report (cortex studies
 * have none); design mirrors SurfacerPanel.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import type { NativePredictions } from "../../types/predictions";
import { getAttributeDescriptors, getTier, getTopSlice } from "../../utils/predictions";
import { reviewerFindingDisplay } from "../../utils/reviewerFindingDisplay";
import { OP_COLORS, OP_FULL_NAMES, type OpLevel } from "../../config/operatingPoints";
import { formatFindingName, formatOrganName } from "../../utils/textFormatters";
import { readerCaseId } from "../../utils/readerCaseId";
import { nextUnreviewedStudy } from "../../utils/reviewerNextCase";
import {
  studyKeys,
  submitDisposition,
  useReviewCohort,
  type DispositionVerdict,
} from "../../hooks/useStudyQueries";
import {
  useReviewerAgreementStats,
  useReviewerFindingVerdicts,
  type FindingReason,
  type FindingVerdict,
  type FindingVerdictEntry,
} from "../../hooks/useReviewerFindingVerdicts";
import { OVERALL_VERDICTS, REASONS } from "./reviewerVerdictOptions";

interface ReviewerPanelProps {
  reviewCohortId?: string;
  studyUid: string;
  predictions: NativePredictions;
  onFindingClick?: (topSliceUid: string) => void;
  /** Optional control rendered on the right of the "Overall assessment" row —
   *  the reviewer inline/list mode-switch gear, so List mode can flip back. */
  modeToggle?: React.ReactNode;
}

interface ReviewerFindingRowProps {
  organ: string;
  finding: string;
  attributes: string[];
  tier: OpLevel | null;
  verdict: FindingVerdict;
  reason: FindingReason | null;
  reasonDetail: string | null;
  clickable: boolean;
  onSetVerdict: (
    verdict: FindingVerdict,
    reason?: FindingReason | null,
    reasonDetail?: string | null,
  ) => void;
  onJumpToSlice: () => void;
}

const ReviewerFindingRow: React.FC<ReviewerFindingRowProps> = ({
  organ,
  finding,
  attributes,
  tier,
  verdict,
  reason,
  reasonDetail,
  clickable,
  onSetVerdict,
  onJumpToSlice,
}) => {
  const agreed = verdict === "agree";
  const disagreed = verdict === "disagree";
  const skipped = verdict === "skip";
  // Title is the bare condition name; the subtitle lists the attribute
  // descriptors (dot-joined). See reviewerFindingDisplay.
  const display = reviewerFindingDisplay({
    fallbackName: formatFindingName(finding),
    attributes,
  });
  // Disagree reveals reason pills before it persists — reason is required, so a
  // bare Disagree click only opens the pills; picking one commits the verdict.
  const [awaitingReason, setAwaitingReason] = useState(false);
  // Free-text detail for the "Other" reason. The verdicts query can resolve
  // after this row mounts, so seed once the persisted detail first arrives
  // (`reasonDetail` starts null while loading). Idempotent thereafter, and the
  // user owns the field the moment they type — `otherSeeded` stops a late query
  // resolution from clobbering an in-progress edit.
  const [otherText, setOtherText] = useState(reasonDetail ?? "");
  const otherSeeded = useRef(false);
  useEffect(() => {
    if (otherSeeded.current || !reasonDetail) return;
    setOtherText(reasonDetail);
    otherSeeded.current = true;
  }, [reasonDetail]);

  // Auto-focus the free-text box the moment the user picks "Other", so they can
  // type immediately. Gate on the transition INTO "other" (not when a row mounts
  // with a previously-saved other-reason) so it doesn't steal focus on open.
  const otherInputRef = useRef<HTMLInputElement>(null);
  const prevReasonRef = useRef(reason);
  useEffect(() => {
    if (disagreed && reason === "other" && prevReasonRef.current !== "other") {
      otherInputRef.current?.focus();
    }
    prevReasonRef.current = reason;
  }, [disagreed, reason]);

  const showPills = disagreed || awaitingReason;

  const borderClass = agreed
    ? "border-l-4 border-l-signal-stroke border-border"
    : disagreed
      ? "border-l-4 border-l-signal-stroke border-border"
      : "border-border hover:border-primary/50 hover:bg-surface-2";

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!clickable) return;
    // The row is role="button"/tabIndex=0, so Enter/Space jump to the slice.
    // But the Disagree → "Other" reason input lives inside this row; its
    // keystrokes bubble here. Don't preventDefault Space (or Enter) when the
    // event originated in an editable element, or the input swallows the space
    // / loses the Enter-to-blur. Bail on the keydown's actual target.
    const t = e.target as HTMLElement | null;
    const tag = t?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || t?.isContentEditable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onJumpToSlice();
    }
  };

  const verdictButton = (
    label: string,
    active: boolean,
    activeClass: string,
    idleClass: string,
    onClick: () => void,
  ) => (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      aria-pressed={active}
      className={`h-7 px-2.5 rounded border text-xs font-semibold transition-colors ${
        active ? activeClass : idleClass
      }`}
    >
      {label}
    </button>
  );

  const reasonPill = (r: { value: FindingReason; label: string }) => {
    const active = disagreed && reason === r.value;
    return (
      <button
        key={r.value}
        onClick={(e) => {
          e.stopPropagation();
          onSetVerdict("disagree", r.value, r.value === "other" ? otherText.trim() || null : null);
          setAwaitingReason(false);
        }}
        aria-pressed={active}
        className={`shrink-0 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors ${
          active
            ? "bg-signal-tint text-signal-text-tint"
            : "text-fg-muted hover:text-fg hover:bg-surface-2"
        }`}
      >
        {r.label}
      </button>
    );
  };

  return (
    <div
      onClick={clickable ? onJumpToSlice : undefined}
      onKeyDown={handleKeyDown}
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`px-3 py-2.5 rounded-md border bg-surface transition-colors space-y-1.5 ${
        clickable ? "cursor-pointer" : ""
      } ${skipped ? "opacity-60" : ""} ${borderClass}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[15px] font-semibold text-fg leading-tight">{display.title}</div>
          {display.subtitle && (
            <div className="mt-0.5 text-[11px] leading-snug text-fg-muted" title={display.subtitle}>
              {display.subtitle}
            </div>
          )}
        </div>
        <div className="flex gap-1.5 items-center shrink-0">
          {verdictButton(
            agreed ? "✓ Agree" : "Agree",
            agreed,
            "bg-signal text-white border-signal-stroke",
            "border-signal-stroke-soft bg-signal-tint text-signal-text-tint hover:bg-signal-tint",
            () => {
              onSetVerdict(agreed ? "pending" : "agree");
              setAwaitingReason(false);
            },
          )}
          {verdictButton(
            disagreed || awaitingReason ? "✕ Disagree" : "Disagree",
            disagreed || awaitingReason,
            "bg-signal text-white border-signal-stroke",
            "border-signal-stroke-soft bg-signal-tint text-signal-text-tint hover:bg-signal-tint",
            () => {
              if (disagreed) {
                onSetVerdict("pending");
                setAwaitingReason(false);
              } else if (awaitingReason) {
                setAwaitingReason(false);
              } else {
                setAwaitingReason(true);
              }
            },
          )}
          {verdictButton(
            "Skip",
            skipped,
            "bg-fg-muted text-bg border-fg-muted",
            "border-border text-fg-dim hover:text-fg hover:bg-surface-2",
            () => {
              onSetVerdict(skipped ? "pending" : "skip");
              setAwaitingReason(false);
            },
          )}
        </div>
      </div>

      {/* Organ + operating point as flat labels (not bordered chips) so they
          read as metadata, not clickable controls. */}
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[10px] font-bold uppercase tracking-wider">
        <span className="text-fg-muted">{formatOrganName(organ)}</span>
        {tier && (
          <>
            <span className="text-fg-faint" aria-hidden="true">
              ·
            </span>
            <span
              className={OP_COLORS[tier].text}
              title="Strictest operating point the AI fired this finding at"
            >
              {OP_FULL_NAMES[tier]}
            </span>
          </>
        )}
      </div>

      {showPills && (
        <div className="space-y-1 pt-0.5" onClick={(e) => e.stopPropagation()}>
          {/* Quick reasons stay on one row; "Other" drops to its own row so its
              free-text box can take the full width without wrapping the pills. */}
          <div className="flex flex-nowrap items-center gap-x-1">
            <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-fg">
              Reason
            </span>
            {REASONS.filter((r) => r.value !== "other").map(reasonPill)}
          </div>
          <div className="flex items-center gap-1.5">
            {REASONS.filter((r) => r.value === "other").map(reasonPill)}
            {disagreed && reason === "other" && (
              <input
                ref={otherInputRef}
                value={otherText}
                onChange={(e) => {
                  otherSeeded.current = true;
                  setOtherText(e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                onBlur={() => onSetVerdict("disagree", "other", otherText.trim() || null)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
                placeholder="Describe…"
                className="min-w-0 flex-1 rounded border border-border bg-surface px-1.5 py-0.5 text-[11px] text-fg placeholder:text-fg-dim focus:border-primary/60 focus:outline-none"
              />
            )}
            {awaitingReason && !disagreed && (
              <span className="ml-auto shrink-0 text-[11px] font-semibold text-signal-text-tint">
                reason required
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const ReviewerPanel: React.FC<ReviewerPanelProps> = ({
  reviewCohortId,
  studyUid,
  predictions,
  onFindingClick,
  modeToggle,
}) => {
  const { token, userId } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // The worklist already runs `useReaderCohort(selectedCohortId)` for this same
  // cohort; this is a deliberate second fetch against the reviewer endpoint
  // (different route + cache key — it carries the caller's dispositions), not a
  // dead duplicate of that query.
  const { data: cohort, isLoading: cohortLoading } = useReviewCohort(
    token,
    reviewCohortId ?? null,
    userId ?? null,
  );
  const study = cohort?.studies.find((s) => s.study_uid === studyUid);
  const disposition = study?.disposition ?? null;

  const { verdicts, setVerdict } = useReviewerFindingVerdicts(
    token,
    userId ?? null,
    reviewCohortId ?? null,
    studyUid,
  );
  const { data: agreementStats } = useReviewerAgreementStats(
    token,
    userId ?? null,
    reviewCohortId ?? null,
  );
  const cohortTotal = cohort?.meta?.total ?? null;

  const findings = useMemo(() => study?.detected_at_recommended ?? [], [study]);
  const groupedByOrgan = useMemo(() => {
    const groups: Record<string, string[]> = {};
    for (const condition of findings) {
      const organ = condition.split("/")[0] || "other";
      (groups[organ] ??= []).push(condition);
    }
    for (const arr of Object.values(groups)) arr.sort((a, b) => a.localeCompare(b));
    return groups;
  }, [findings]);

  const verdictFor = (condition: string): FindingVerdictEntry =>
    verdicts[condition] ?? { verdict: "pending", reason: null, reason_detail: null };
  let agreedCount = 0;
  let disagreedCount = 0;
  for (const c of findings) {
    const v = verdictFor(c).verdict;
    if (v === "agree") agreedCount += 1;
    else if (v === "disagree") disagreedCount += 1;
  }
  const remainingCount = findings.length - agreedCount - disagreedCount;

  // Overall verdict + note — seeded once from the existing disposition, then
  // owned by the user. `seededRef` keeps the seed idempotent: it no-ops once a
  // disposition has been applied, so a background refetch that mints a fresh
  // disposition object can't clobber edits. The edit handlers also stamp the
  // ref (`markOwned`), so a disposition arriving late (the cohort query is
  // async) can't overwrite an assessment the reviewer started before it loaded.
  const [overall, setOverall] = useState<DispositionVerdict | null>(null);
  const [note, setNote] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const seededRef = useRef(false);
  const markOwned = () => {
    seededRef.current = true;
  };
  useEffect(() => {
    if (seededRef.current || !disposition) return;
    setOverall(disposition.verdict);
    setNote(disposition.comment ?? "");
    // An already-reviewed case opens directly in the saved state (Save collapses
    // to half width + "Next case" appears) — no re-save needed this session.
    setJustSaved(true);
    seededRef.current = true;
  }, [disposition]);

  const saveMutation = useMutation({
    mutationFn: () =>
      submitDisposition(token!, {
        cohortId: reviewCohortId!,
        studyUid,
        verdict: overall!,
        comment: note.trim() ? note.trim() : null,
      }),
    onSuccess: () => {
      setJustSaved(true);
      setSaveError(null);
      queryClient.invalidateQueries({
        queryKey: studyKeys.reviewCohort(reviewCohortId ?? "", userId ?? ""),
      });
    },
    onError: (err) => {
      // The disposition PutItem is idempotent (deterministic SK), so a retry is
      // safe — but the reviewer must know the save didn't land. Surface the
      // message next to Save and log it; never swallow it silently.
      const detail = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : err instanceof Error
          ? err.message
          : "Failed to save assessment";
      console.error("[reviewer] disposition save failed", err);
      setSaveError(detail);
    },
  });

  // Post-save navigation target: the next case in the cohort (wrapping) with no
  // disposition yet. null when the current case is the only unreviewed one left.
  // Excludes the current study.
  const nextUnreviewed = useMemo(
    () => nextUnreviewedStudy(cohort?.studies ?? [], studyUid),
    [cohort, studyUid],
  );

  const goToNextCase = () => {
    if (!nextUnreviewed || !reviewCohortId) return;
    const composite = readerCaseId(nextUnreviewed);
    navigate(`/case-viewer/${composite}?cohort=${encodeURIComponent(reviewCohortId)}`);
  };

  if (!reviewCohortId) {
    return (
      <div className="h-full flex items-center justify-center text-center text-sm text-fg-dim px-6">
        Open this case from your review worklist to leave feedback.
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b border-border flex items-center gap-2 bg-primary/5">
        <span className="text-[11px] font-bold uppercase tracking-widest text-primary-300">
          AI Findings
        </span>
        <div className="ml-auto text-xs text-fg-muted flex items-center gap-2">
          <span>
            <strong className="text-signal-text-tint font-semibold">{agreedCount}</strong> agreed
          </span>
          <span className="text-fg-faint">·</span>
          <span>
            <strong className="text-signal-text-tint font-semibold">{disagreedCount}</strong>{" "}
            disagreed
          </span>
          <span className="text-fg-faint">·</span>
          <span>
            <strong className="text-fg font-semibold">{remainingCount}</strong> remaining
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2 flex flex-col gap-3">
        {cohortLoading ? (
          <div className="flex-1 flex items-center justify-center text-sm text-fg-dim">
            Loading findings…
          </div>
        ) : findings.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-center text-sm text-fg-dim px-6">
            No AI findings were flagged on this study. You can still leave an overall assessment
            below.
          </div>
        ) : (
          Object.entries(groupedByOrgan)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([organ, conditions]) => (
              <div key={organ} className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase tracking-widest text-fg-dim px-1">
                  {formatOrganName(organ)}
                </span>
                {conditions.map((condition) => {
                  const pred = predictions[condition];
                  const tier = pred ? getTier(pred) : null;
                  const topSlice = pred ? getTopSlice(pred) : null;
                  const attributes = getAttributeDescriptors(condition, predictions);
                  const finding = condition.split("/").slice(1).join("/");
                  const entry = verdictFor(condition);
                  return (
                    <ReviewerFindingRow
                      key={condition}
                      organ={organ}
                      finding={finding}
                      attributes={attributes}
                      tier={tier}
                      verdict={entry.verdict}
                      reason={entry.reason}
                      reasonDetail={entry.reason_detail}
                      clickable={!!topSlice}
                      onSetVerdict={(v, r, rd) => setVerdict(condition, v, r ?? null, rd ?? null)}
                      onJumpToSlice={() => {
                        if (topSlice) onFindingClick?.(topSlice);
                      }}
                    />
                  );
                })}
              </div>
            ))
        )}
      </div>

      <div className="shrink-0 border-t border-border bg-surface p-3 space-y-2">
        <div className="flex items-center justify-between pb-2 border-b border-border">
          <span className="text-[10px] font-bold uppercase tracking-widest text-fg-dim">
            Cohort · agreement
          </span>
          <span className="flex items-center gap-3 text-[11px] text-fg-muted">
            <span title="Agreed / (agreed + disagreed) across this cohort — your read, no reference standard">
              Agreement{" "}
              <strong className="text-fg font-semibold">
                {agreementStats?.agreement_rate != null
                  ? `${Math.round(agreementStats.agreement_rate * 100)}%`
                  : "—"}
              </strong>
            </span>
            <span>
              Reviewed{" "}
              <strong className="text-fg font-semibold">
                {agreementStats?.cases_reviewed ?? 0}
                {cohortTotal != null ? `/${cohortTotal}` : ""}
              </strong>
            </span>
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-bold uppercase tracking-widest text-fg-muted">
            Overall assessment
          </span>
          {modeToggle}
        </div>
        <div className="flex gap-1.5">
          {OVERALL_VERDICTS.map((v) => {
            const selected = overall === v.value;
            return (
              <button
                key={v.value}
                onClick={() => {
                  markOwned();
                  setOverall(v.value);
                  setJustSaved(false);
                  setSaveError(null);
                }}
                aria-pressed={selected}
                className={`flex-1 h-8 rounded border text-xs font-semibold transition-colors ${
                  selected ? v.active : "border-border text-fg-muted hover:bg-surface-2"
                }`}
              >
                {v.label}
              </button>
            );
          })}
        </div>
        <textarea
          value={note}
          onChange={(e) => {
            markOwned();
            setNote(e.target.value);
            setJustSaved(false);
            setSaveError(null);
          }}
          placeholder="Note (e.g. AI missed a crucial finding)…"
          rows={2}
          className="w-full resize-none rounded border border-border bg-surface px-2 py-1.5 text-xs text-fg placeholder:text-fg-dim focus:border-primary/60 focus:outline-none"
        />
        {/* Save keeps the same primary color in every state (full-width, or
            "Saved ✓" at half width). When saved it shrinks and "Next case" grows
            in from the right via a width transition; un-saved, Next collapses to
            zero width (not just hidden) so the right edge animates smoothly
            rather than popping. Clicking the saved Save un-saves — re-enables
            editing and collapses Next (frontend only; the persisted disposition
            is untouched, matching the edit-after-save behavior). */}
        <div className="flex items-center">
          <button
            onClick={justSaved ? () => setJustSaved(false) : () => saveMutation.mutate()}
            disabled={!justSaved && (!overall || saveMutation.isPending)}
            className="flex-1 h-8 rounded bg-primary text-white text-xs font-semibold transition-colors hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending ? "Saving…" : justSaved ? "Saved ✓" : "Save assessment"}
          </button>
          <button
            onClick={goToNextCase}
            disabled={!nextUnreviewed}
            aria-hidden={!justSaved}
            tabIndex={justSaved ? 0 : -1}
            title={
              justSaved && !nextUnreviewed ? "No more unreviewed cases in this cohort" : undefined
            }
            className={`h-8 shrink-0 overflow-hidden whitespace-nowrap rounded border border-primary text-primary text-xs font-semibold transition-all duration-200 ease-out hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed ${
              justSaved
                ? "ml-1.5 w-1/2 px-2 opacity-100"
                : "ml-0 w-0 border-0 px-0 opacity-0 pointer-events-none"
            }`}
          >
            {nextUnreviewed ? "Next case →" : "All reviewed"}
          </button>
        </div>
        {saveError && (
          <p data-testid="disposition-error" className="text-[11px] text-warn font-semibold">
            Couldn’t save — {saveError}
          </p>
        )}
      </div>
    </div>
  );
};

export default ReviewerPanel;
